import { useRef, useEffect, useState, useCallback } from 'react'

/**
 * 🎯 Hook для стабилизации высоты элементов
 * 
 * Запоминает высоту элемента после загрузки данных и применяет её
 * как min-height во время загрузки, предотвращая "скачки" макета
 */
export function useStableHeight(
  key: string,
  isLoading?: boolean,
  hasData?: boolean
) {
  const elementRef = useRef<HTMLDivElement>(null)
  const [savedHeight, setSavedHeight] = useState<number | null>(null)
  
  // Загружаем сохраненную высоту из localStorage при инициализации
  useEffect(() => {
    try {
      const stored = localStorage.getItem(`stable-height-${key}`)
      if (stored) {
        const height = parseInt(stored, 10)
        if (!isNaN(height) && height > 0) {
          setSavedHeight(height)
        }
      }
    } catch (error) {
      console.warn('[useStableHeight] Ошибка чтения из localStorage:', error)
    }
  }, [key])

  // Измеряем и сохраняем высоту когда данные загружены
  const measureAndSave = useCallback(() => {
    if (!elementRef.current) return
    
    try {
      const element = elementRef.current
      const rect = element.getBoundingClientRect()
      
      if (rect.height > 0) {
        const newHeight = Math.ceil(rect.height)
        
        // Обновляем только если высота изменилась значительно (больше чем на 5px)
        if (!savedHeight || Math.abs(newHeight - savedHeight) > 5) {
          setSavedHeight(newHeight)
          localStorage.setItem(`stable-height-${key}`, newHeight.toString())
          console.log(`[useStableHeight] Обновлена высота ${savedHeight}px → ${newHeight}px для ${key}`)
        }
      }
    } catch (error) {
      console.warn('[useStableHeight] Ошибка измерения высоты:', error)
    }
  }, [key, savedHeight])

  // Измеряем высоту после завершения загрузки
  useEffect(() => {
    if (!isLoading && hasData) {
      // Небольшая задержка для завершения рендера
      const timeout = setTimeout(measureAndSave, 150)
      return () => clearTimeout(timeout)
    }
  }, [isLoading, hasData, measureAndSave])

  // Возвращаем ref и стили ТОЛЬКО ДЛЯ СОСТОЯНИЯ ЗАГРУЗКИ
  return {
    ref: elementRef,
    minHeight: savedHeight ? `${savedHeight}px` : undefined,
    // style применяется ТОЛЬКО во время загрузки!
    style: (isLoading && savedHeight) ? { minHeight: `${savedHeight}px` } : undefined
  }
}
